import { CheveronDownIcon } from '@wkda/icons';
import cx from 'classnames';
import type { ElementType, ReactNode } from 'react';
import { useState } from 'react';
import type { UserNavListItem } from '../shared.types.js';

export interface UserNavMenuProps {
  LinkComponent?: ElementType;
  navList: UserNavListItem[];
  className?: string;
}

export interface UserNavMenuState {
  openedTitles: string[];
}

export default function UserNavMenu({
  LinkComponent = 'a',
  navList,
  className,
}: UserNavMenuProps) {
  const [openedTitles, setOpenedTitles] = useState<string[]>([]);

  function toggleDropDown(title: string) {
    const index = openedTitles.indexOf(title);
    setOpenedTitles(
      index < 0
        ? openedTitles.concat(title)
        : [...openedTitles.slice(0, index), ...openedTitles.slice(index + 1)],
    );
  }

  function wrapWithLink(link: string, children: ReactNode) {
    return link && LinkComponent ? (
      <LinkComponent className="wa-nav-user__item-link" href={link}>
        {children}
      </LinkComponent>
    ) : (
      children
    );
  }

  function renderList(
    { title = '', items, link = '', separator }: UserNavListItem,
    key: number,
    level = 1,
  ) {
    const itemHasDropdown = items != null && items.length > 0;
    const isOpened = openedTitles.indexOf(title) > -1;

    if (separator) {
      return (
        <li key={`separator-${key}`} className="wa-nav-user__item-separator" />
      );
    }

    return (
      <li className={cx('wa-nav-user__item')} key={key}>
        {wrapWithLink(
          link,
          <>
            <div
              key="title-wrapper"
              className={cx(
                'wa-nav-user__item-wrapper',
                `wa-nav-user__item-wrapper--level-${level}`,
                {
                  'wa-nav-user__item-wrapper--has-dropdown': itemHasDropdown,
                  'wa-nav-user__item-wrapper--active': isOpened,
                },
              )}
              onClick={
                itemHasDropdown ? () => toggleDropDown(title) : undefined
              }
            >
              <div
                key="item-title"
                className="wa-nav-user__item-title"
                onClick={
                  itemHasDropdown ? () => toggleDropDown(title) : undefined
                }
              >
                {title}
              </div>
              {itemHasDropdown && (
                <CheveronDownIcon
                  className={cx('wa-nav-user__chevron', {
                    'wa-nav-user__chevron--is-open': isOpened,
                  })}
                />
              )}
            </div>
            {itemHasDropdown && isOpened && (
              <ul key="item-sublist" className="wa-nav-user__list">
                {(items ?? []).map((item, index) =>
                  renderList(item, index, level + 1),
                )}
              </ul>
            )}
          </>,
        )}
      </li>
    );
  }

  return (
    <div className={cx('wa-nav-user', className)}>
      <ul className="wa-nav-user__list">
        {navList.map((item, index) => renderList(item, index))}
      </ul>
    </div>
  );
}
